import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';

import { AuthEvent, vaultCause, VaultTokenStorage } from '@ninety/mobile/platform/auth';

import { CommandRunner } from './command-runner.service';

/**
 * The Vault Commands — bytes at rest, and nothing that prompts.
 *
 * Every one of these is a launch-path Command, so the rule they share is that none of them may
 * leave the machine without an answer: a launch that cannot reach a decided state is a splash
 * screen that never goes away, which is worse than a sign-in prompt.
 *
 * Everything biometric lives in `SessionBiometricsEffects`.
 *
 * Note the absence of a handler for persisting the Durable Root. Amplify never exposes the
 * Cognito refresh token, so the app cannot write it — it owns the storage Amplify writes
 * through instead, and `VaultTokenStorage` does that routing.
 */
@Injectable()
export class SessionVaultEffects {
  private readonly commands = inject(CommandRunner);
  private readonly storage = inject(VaultTokenStorage);

  readonly primeTokenStorage$ = createEffect(() =>
    this.commands.on('primeTokenStorage', async (): Promise<AuthEvent> => {
      try {
        return { type: 'tokenStorageLoaded', hasSession: await this.storage.prime() };
      } catch (error) {
        return { type: 'primingFailed', cause: vaultCause(error) };
      }
    })
  );

  /** Teardown, so there is no outcome for the machine to decide anything about. */
  readonly clearVault$ = createEffect(() => this.commands.teardown('clearVault', () => this.storage.clear()));
}
